/**
 * StorageService.ts
 *
 * Centralized, type-safe localStorage management.
 * Replaces scattered localStorage calls throughout the codebase.
 */

import { gameLogger } from '../../utils/GameLogger';
import {
  parseUserPrefs,
  USER_PREFS_DEFAULTS,
  type UserPrefs,
} from '../../shared/userprefs/schema';

export type StorageKey =
  | 'helix_jwt'
  | 'helix_god_mode'
  | 'helix_noclip'
  | 'helix_force_login'
  | 'helix_physics_profiles'
  | 'helix_camera_profiles'
  | 'helix_new_character_appearance'
  | 'helix_force_remote_logging'
  | 'helix_keybindings'
  | 'helix_user_prefs'
  | 'chat_history'
  | 'show_dev_action_logs'
  // Last-selected character persisted on character-select so `?keepCharacter=1`
  // flows (editor Test button, mode cards) can auto-re-enter with the same
  // character instead of re-showing the picker. See PreGameFlow.
  | 'helix_last_character'
  // Phase 1.5 of Twitch auth migration arc — synchronous hint set after a
  // successful /api/auth/twitch/session probe, used by useAuth to suppress
  // the dev-auto-spawn flash on Twitch users opening the app in a new tab.
  // Cleared on twitchLogout or when a probe reports no authenticated session.
  | 'helix_twitch_recent';

const USER_PREFS_KEY: StorageKey = 'helix_user_prefs';
const LEGACY_AUDIO_KEY = 'helix_audio_settings';
type UserPrefsListener = (next: UserPrefs) => void;
const userPrefsListeners = new Set<UserPrefsListener>();

/**
 * Type-safe storage service for localStorage
 */
export class StorageService {
  private static instance: StorageService;

  private constructor() {}

  public static getInstance(): StorageService {
    if (!StorageService.instance) {
      StorageService.instance = new StorageService();
    }
    return StorageService.instance;
  }

  /**
   * Get an item from localStorage
   */
  public get(key: StorageKey): string | null {
    if (typeof window === 'undefined') return null;
    try {
      return localStorage.getItem(key);
    } catch (e) {
      gameLogger.warn('storage', `[StorageService] Failed to get ${key}:`, e);
      return null;
    }
  }

  /**
   * Set an item in localStorage
   */
  public set(key: StorageKey, value: string): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      gameLogger.warn('storage', `[StorageService] Failed to set ${key}:`, e);
    }
  }

  /**
   * Remove an item from localStorage
   */
  public remove(key: StorageKey): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem(key);
    } catch (e) {
      gameLogger.warn('storage', `[StorageService] Failed to remove ${key}:`, e);
    }
  }

  /**
   * Get a boolean value (stored as 'true'/'false' or presence check)
   */
  public getBoolean(key: StorageKey): boolean {
    return this.get(key) === 'true';
  }

  /**
   * Set a boolean value
   */
  public setBoolean(key: StorageKey, value: boolean): void {
    if (value) {
      this.set(key, 'true');
    } else {
      this.remove(key);
    }
  }

  /**
   * Get a JSON-parsed object
   */
  public getObject<T>(key: StorageKey): T | null {
    const item = this.get(key);
    if (!item) return null;
    try {
      return JSON.parse(item) as T;
    } catch (e) {
      gameLogger.warn('storage', `[StorageService] Failed to parse ${key} as JSON:`, e);
      return null;
    }
  }

  /**
   * Set a JSON-stringified object
   */
  public setObject<T>(key: StorageKey, value: T): void {
    try {
      this.set(key, JSON.stringify(value));
    } catch (e) {
      gameLogger.warn('storage', `[StorageService] Failed to stringify ${key}:`, e);
    }
  }

  /**
   * Check if a key exists
   */
  public has(key: StorageKey): boolean {
    return this.get(key) !== null;
  }

  /**
   * Toggle a boolean value
   * Returns the new value
   */
  public toggle(key: StorageKey): boolean {
    const newValue = !this.getBoolean(key);
    this.setBoolean(key, newValue);
    return newValue;
  }

  // =============================================================================
  // RAW KEY METHODS (for dynamic/component-specific keys)
  // =============================================================================

  /**
   * Get an item with a raw string key (for dynamic keys)
   */
  public getRaw(key: string): string | null {
    if (typeof window === 'undefined') return null;
    try {
      return localStorage.getItem(key);
    } catch (e) {
      gameLogger.warn('storage', `[StorageService] Failed to get ${key}:`, e);
      return null;
    }
  }

  /**
   * Set an item with a raw string key (for dynamic keys)
   */
  public setRaw(key: string, value: string): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      gameLogger.warn('storage', `[StorageService] Failed to set ${key}:`, e);
    }
  }

  /**
   * Remove an item with a raw string key
   */
  public removeRaw(key: string): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem(key);
    } catch (e) {
      gameLogger.warn('storage', `[StorageService] Failed to remove ${key}:`, e);
    }
  }

  /**
   * Get a JSON object with a raw string key
   */
  public getObjectRaw<T>(key: string): T | null {
    const item = this.getRaw(key);
    if (!item) return null;
    try {
      return JSON.parse(item) as T;
    } catch (e) {
      gameLogger.warn('storage', `[StorageService] Failed to parse ${key} as JSON:`, e);
      return null;
    }
  }

  /**
   * Set a JSON object with a raw string key
   */
  public setObjectRaw<T>(key: string, value: T): void {
    try {
      this.setRaw(key, JSON.stringify(value));
    } catch (e) {
      gameLogger.warn('storage', `[StorageService] Failed to stringify ${key}:`, e);
    }
  }

  // =============================================================================
  // COLD-CLONE PROBE — does localStorage actually persist on this browser?
  // =============================================================================

  /**
   * Probe whether localStorage writes are actually persisted in the current
   * environment. Returns false in private-mode tabs, when quota is exhausted,
   * or when running outside the browser. Cached after the first call so the
   * probe doesn't run on every read.
   *
   * Mirrors DJ-Engine settings rule #5 (cold-clone safety): a fresh visitor
   * on a constrained browser still gets a working game; their preferences
   * just don't survive the tab close.
   */
  public isPersistent(): boolean {
    if (this.persistenceProbeResult !== null) return this.persistenceProbeResult;
    if (typeof window === 'undefined') {
      this.persistenceProbeResult = false;
      return false;
    }
    try {
      const probeKey = '__helix_persistence_probe__';
      localStorage.setItem(probeKey, '1');
      const readback = localStorage.getItem(probeKey);
      localStorage.removeItem(probeKey);
      this.persistenceProbeResult = readback === '1';
    } catch {
      this.persistenceProbeResult = false;
    }
    return this.persistenceProbeResult;
  }

  private persistenceProbeResult: boolean | null = null;

  // =============================================================================
  // USER PREFS — single round-trip blob, Zod-validated, never throws
  // =============================================================================

  /**
   * Load the unified UserPrefs blob from localStorage.
   *
   * - Returns full defaults if the key is unset, the JSON is malformed, or
   *   the schema fails validation.
   * - Performs a one-time migration of `helix_audio_settings` (legacy 0-100
   *   ints) into the new blob (0-1 floats) the first time it's called after
   *   a fresh visit. After migration the legacy key is removed.
   */
  public loadUserPrefs(): UserPrefs {
    const stored = this.get(USER_PREFS_KEY);
    if (stored) {
      try {
        return parseUserPrefs(JSON.parse(stored));
      } catch {
        // Malformed JSON — fall through to default + try legacy migration
      }
    }

    // No (valid) blob yet — try migrating legacy audio settings
    const migrated = this.migrateLegacyAudio();
    if (migrated) {
      this.saveUserPrefs(migrated, { silent: true });
      return migrated;
    }

    return USER_PREFS_DEFAULTS;
  }

  /**
   * Persist the UserPrefs blob and notify subscribers in the same tab.
   * Cross-tab updates are picked up by the `storage` event listener
   * installed by `subscribeUserPrefs` consumers.
   */
  public saveUserPrefs(next: UserPrefs, opts?: { silent?: boolean }): void {
    try {
      this.set(USER_PREFS_KEY, JSON.stringify(next));
    } catch (e) {
      gameLogger.warn('storage', '[StorageService] Failed to save user prefs:', e);
      return;
    }
    if (!opts?.silent) {
      this.emitUserPrefs(next);
    }
  }

  /**
   * Subscribe to UserPrefs changes from any source — same-tab saves AND
   * cross-tab `storage` events. Returns an unsubscribe fn.
   *
   * Mirrors the DJ-Engine pattern of "settings update without reload":
   * GameAudioSystem / KeyBindingManager / etc. subscribe at construction
   * and react to every mutation immediately.
   */
  public subscribeUserPrefs(listener: UserPrefsListener): () => void {
    userPrefsListeners.add(listener);
    this.ensureCrossTabBridge();
    return () => {
      userPrefsListeners.delete(listener);
    };
  }

  private emitUserPrefs(next: UserPrefs): void {
    for (const fn of userPrefsListeners) {
      try {
        fn(next);
      } catch (e) {
        gameLogger.warn('storage', '[StorageService] user-prefs listener threw:', e);
      }
    }
  }

  private crossTabBridgeInstalled = false;
  private ensureCrossTabBridge(): void {
    if (this.crossTabBridgeInstalled) return;
    if (typeof window === 'undefined') return;
    window.addEventListener('storage', (e) => {
      if (e.key !== USER_PREFS_KEY) return;
      this.emitUserPrefs(this.loadUserPrefs());
    });
    this.crossTabBridgeInstalled = true;
  }

  /**
   * One-time migration from the pre-Phase-B `helix_audio_settings` key.
   * Returns a fresh UserPrefs with audio populated from the legacy blob,
   * or null if no legacy data is present.
   */
  private migrateLegacyAudio(): UserPrefs | null {
    if (typeof window === 'undefined') return null;
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(LEGACY_AUDIO_KEY);
    } catch {
      return null;
    }
    if (!raw) return null;
    try {
      const legacy = JSON.parse(raw) as {
        master?: number;
        music?: number;
        sfx?: number;
      };
      // Legacy stored 0-100; canonical is 0-1.
      const next: UserPrefs = {
        ...USER_PREFS_DEFAULTS,
        audio: {
          ...USER_PREFS_DEFAULTS.audio,
          masterVolume: clamp01((legacy.master ?? 50) / 100),
          musicVolume: clamp01((legacy.music ?? 30) / 100),
          sfxVolume: clamp01((legacy.sfx ?? 70) / 100),
        },
      };
      try {
        localStorage.removeItem(LEGACY_AUDIO_KEY);
      } catch { /* best-effort */ }
      return next;
    } catch {
      return null;
    }
  }
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

// Export singleton instance for convenience
export const storage = StorageService.getInstance();
